import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader, ErrorState, EmptyState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { CategoryChips, OppScore, MiniMetric, OpportunityLink } from "@/components/opportunity";
import { Sparkles, Target, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function Opportunities() {
  const [state, setState] = useState({ loading: true, error: null });
  const [items, setItems] = useState([]);
  const [sort, setSort] = useState("score");
  const [generating, setGenerating] = useState(false);
  const [gate, setGate] = useState(null);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const { data } = await api.get("/opportunities");
      setItems(data.opportunities || []);
      setGate(null);
      setState({ loading: false, error: null });
    } catch (err) { setState({ loading: false, error: apiErrorMessage(err) }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setGate(null);
    try {
      const { data } = await api.post("/opportunities/generate", { count: 8 });
      setItems(data.opportunities || []);
      toast.success(`Generated ${(data.opportunities || []).length} opportunities`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setGate(typeof detail === "object" ? detail : null);
      toast.error(apiErrorMessage(err));
    } finally { setGenerating(false); }
  };

  const ordered = useMemo(() =>
    [...items].sort((a, b) =>
      sort === "confidence" ? b.confidence_score - a.confidence_score :
      sort === "evidence" ? b.source_count - a.source_count :
      b.opportunity_score - a.opportunity_score
    ), [items, sort]);

  return (
    <AppShell title="Opportunities">
      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-accent">Intelligence</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-foreground">Content Opportunities</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Ranked by your Creator DNA and real evidence.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-xl border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
            aria-label="Sort opportunities">
            <option value="score">Opportunity score</option>
            <option value="confidence">Confidence</option>
            <option value="evidence">Evidence</option>
          </select>
          <Button onClick={generate} disabled={generating}
            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm">
            <Sparkles className="mr-2 h-4 w-4" />
            {generating ? "Generating…" : "Generate"}
          </Button>
        </div>
      </div>

      {state.loading ? <Loader label="Loading opportunities" /> :
       state.error ? <ErrorState message={state.error} onRetry={load} /> :
       gate ? (
        <EmptyState icon={Target} testid={`opportunities-${gate.code || "gate"}`}
          title={gate.code === "needs_dna" ? "Complete your Creator DNA" : gate.code === "no_snapshot" ? "Run Intelligence first" : "More research is needed"}
          description={gate.message}
          action={<Button variant="outline" onClick={() => window.location.assign(gate.code === "needs_dna" ? "/onboarding" : "/intelligence")}>{gate.code === "needs_dna" ? "Open Creator DNA" : "Open Intelligence"}</Button>} />
       ) : ordered.length === 0 ? (
        <EmptyState icon={Target} testid="opportunities-empty"
          title="No opportunities generated yet"
          description="Generate opportunities from your latest Intelligence snapshot when it is ready." />
       ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="opportunities-list">
          {ordered.map((o) => (
            <OpportunityLink key={o.id} opportunity={o}>
              <article className="card-hover group rounded-2xl border border-border bg-surface p-4 sm:p-6 shadow-sm transition-all hover:border-accent/40" data-testid="opportunity-card">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <CategoryChips categories={o.categories} />
                    <h3 className="mt-3 font-display text-lg font-bold text-foreground leading-snug">{o.title}</h3>
                  </div>
                  <OppScore score={o.opportunity_score} confidence={o.confidence_score} />
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-2">{o.why_now || o.problem || o.market_note}</p>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <MiniMetric label="Demand" value={o.scores?.demand} />
                  <MiniMetric label="Growth" value={o.scores?.growth} />
                  <MiniMetric label="Evidence" value={o.evidence_item_ids?.length || 0} />
                </div>
                <div className="mt-4 flex items-center justify-end">
                  <span className="flex items-center gap-1 text-xs font-semibold text-accent opacity-0 transition-opacity group-hover:opacity-100">
                    View detail <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </article>
            </OpportunityLink>
          ))}
        </div>
       )}
    </AppShell>
  );
}
